import React, { useEffect, useState } from 'react';
import apiService from './apiService';
import { useAuth } from './contexts/AuthContext';
import { useAlert } from './contexts/AlertContext';
import { FileText } from 'lucide-react';

const statusOptions = ['pending', 'paid', 'overdue', 'cancelled'];

const Invoices = () => {
    const { currentUser } = useAuth();
    const { showAlert } = useAlert();
    const [invoices, setInvoices] = useState([]);
    const [organizations, setOrganizations] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedOrg, setSelectedOrg] = useState(null);
    const [showCreateForm, setShowCreateForm] = useState(false);
    const [newInvoice, setNewInvoice] = useState({
        organizationId: '',
        description: '',
        amount: '',
        dueDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
        notes: ''
    });

    useEffect(() => {
        loadData();
    }, [selectedOrg]);

    const loadData = async () => {
        let mounted = true;
        try {
            const [invoiceList, orgList] = await Promise.all([
                apiService.getInvoices({ organizationId: selectedOrg }),
                currentUser.role === 'superadmin' ? apiService.getOrganizations() : Promise.resolve([])
            ]);
            if (mounted) {
                setInvoices(invoiceList);
                setOrganizations(orgList);
            }
        } catch (e) {
            showAlert('Failed to load data', 'error');
        } finally {
            if (mounted) setLoading(false);
        }
    };

    const handleMarkPaid = async (invoiceId, paymentMethod, paymentReference) => {
        try {
            await apiService.markInvoicePaid({ invoiceId, paymentMethod, paymentReference });
            setInvoices(prev => prev.map(inv => inv.id === invoiceId ? { ...inv, status: 'paid', paidAt: new Date().toISOString() } : inv));
            showAlert('Invoice marked as paid', 'success');
        } catch (e) {
            showAlert('Failed to update invoice', 'error');
        }
    };

    const handleCreateInvoice = async (e) => {
        e.preventDefault();
        try {
            const lineItems = [newInvoice.description];
            await apiService.createInvoice({
                organizationId: parseInt(newInvoice.organizationId),
                lineItems,
                subtotal: parseFloat(newInvoice.amount),
                dueDate: new Date(newInvoice.dueDate).toISOString(),
                notes: newInvoice.notes
            });
            showAlert('Invoice created successfully', 'success');
            setShowCreateForm(false);
            setNewInvoice({
                organizationId: '',
                description: '',
                amount: '',
                dueDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
                notes: ''
            });
            loadData();
        } catch (e) {
            showAlert('Failed to create invoice', 'error');
        }
    };

    if (currentUser?.role !== 'superadmin') {
        return <div className="p-6 text-red-600">Only superadmin can manage invoices.</div>;
    }

    return (
        <div className="space-y-6">
            <div className="flex items-center justify-between">
                <div>
                    <h2 className="text-2xl font-bold">Invoice Management (Superadmin)</h2>
                    <p className="text-gray-600 dark:text-gray-400">Create and manage all organization invoices</p>
                </div>
                {currentUser?.role === 'superadmin' && (
                    <button
                        onClick={() => setShowCreateForm(!showCreateForm)}
                        className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
                    >
                        {showCreateForm ? 'Cancel' : '+ Create Invoice'}
                    </button>
                )}
            </div>

            {/* Create Invoice Form */}
            {showCreateForm && currentUser?.role === 'superadmin' && (
                <div className="bg-white dark:bg-gray-800 p-6 rounded-lg border border-blue-200">
                    <h3 className="text-lg font-semibold mb-4">Create New Invoice</h3>
                    <form onSubmit={handleCreateInvoice} className="space-y-4">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <label className="block text-sm font-medium mb-1">Organization</label>
                                <select
                                    required
                                    value={newInvoice.organizationId}
                                    onChange={(e) => setNewInvoice({ ...newInvoice, organizationId: e.target.value })}
                                    className="w-full border p-2 rounded"
                                >
                                    <option value="">Select organization...</option>
                                    {organizations.map(org => (
                                        <option key={org.id} value={org.id}>{org.name}</option>
                                    ))}
                                </select>
                            </div>
                            <div>
                            <label className="block text-sm font-medium mb-1">Amount (USD)</label>
                            <input
                                type="number"
                                required
                                step="0.01"
                                value={newInvoice.amount}
                                onChange={(e) => setNewInvoice({ ...newInvoice, amount: e.target.value })}
                                className="w-full border p-2 rounded"
                                placeholder="100"
                            />
                            </div>
                            <div>
                                <label className="block text-sm font-medium mb-1">Description</label>
                                <input
                                    type="text"
                                    required
                                    value={newInvoice.description}
                                    onChange={(e) => setNewInvoice({ ...newInvoice, description: e.target.value })}
                                    className="w-full border p-2 rounded"
                                    placeholder="Monthly subscription - Hospital Plan"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium mb-1">Due Date</label>
                                <input
                                    type="date"
                                    required
                                    value={newInvoice.dueDate}
                                    onChange={(e) => setNewInvoice({ ...newInvoice, dueDate: e.target.value })}
                                    className="w-full border p-2 rounded"
                                />
                            </div>
                        </div>
                        <div>
                            <label className="block text-sm font-medium mb-1">Notes (Optional)</label>
                            <textarea
                                value={newInvoice.notes}
                                onChange={(e) => setNewInvoice({ ...newInvoice, notes: e.target.value })}
                                className="w-full border p-2 rounded"
                                rows="2"
                                placeholder="Additional notes..."
                            />
                        </div>
                        <button
                            type="submit"
                            className="w-full bg-blue-600 text-white py-2 rounded-lg hover:bg-blue-700"
                        >
                            Create Invoice
                        </button>
                    </form>
                </div>
            )}

            <div className="bg-white dark:bg-gray-800 rounded-lg overflow-hidden">
                {loading ? (
                    <div className="p-6">Loading invoices…</div>
                ) : invoices.length === 0 ? (
                    <div className="p-8 text-center text-gray-500">
                        <FileText className="w-12 h-12 mx-auto mb-3 opacity-50" />
                        <p>No invoices found</p>
                    </div>
                ) : (
                    <table className="w-full text-left">
                        <thead className="bg-gray-50 dark:bg-gray-700 border-b">
                            <tr>
                                <th className="p-3">Invoice #</th>
                                <th className="p-3">Organization</th>
                                <th className="p-3">Date</th>
                                <th className="p-3">Due Date</th>
                                <th className="p-3">Amount</th>
                                <th className="p-3">Status</th>
                                <th className="p-3">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {invoices.map(inv => (
                                <tr key={inv.id} className="border-b hover:bg-gray-50 dark:hover:bg-gray-700">
                                    <td className="p-3 font-mono text-sm">{inv.invoiceNumber}</td>
                                    <td className="p-3">Org #{inv.organizationId}</td>
                                    <td className="p-3">{new Date(inv.invoiceDate).toLocaleDateString()}</td>
                                    <td className="p-3">{new Date(inv.dueDate).toLocaleDateString()}</td>
                                    <td className="p-3 font-semibold">${inv.totalAmount?.toLocaleString()}</td>
                                    <td className="p-3">
                                        <span className={`px-2 py-1 rounded text-xs ${
                                            inv.status === 'paid' ? 'bg-green-100 text-green-700' :
                                            inv.status === 'pending' ? 'bg-yellow-100 text-yellow-700' :
                                            inv.status === 'overdue' ? 'bg-red-100 text-red-700' :
                                            'bg-gray-100 text-gray-700'
                                        }`}>
                                            {inv.status}
                                        </span>
                                    </td>
                                    <td className="p-3">
                                        {inv.status === 'pending' && (
                                            <button 
                                                onClick={() => handleMarkPaid(inv.id, 'mpesa', 'MANUAL-PAYMENT')} 
                                                className="px-3 py-1 bg-green-600 text-white rounded text-sm hover:bg-green-700"
                                            >
                                                Mark Paid
                                            </button>
                                        )}
                                        {inv.status === 'paid' && (
                                            <span className="text-green-600 text-sm">Paid on {new Date(inv.paidAt).toLocaleDateString()}</span>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>
        </div>
    );
};

export default Invoices;


